/**
 * Utility Functions
 */
const Utils = {
  parseJSON(string) {
    try {
      return JSON.parse(string);
    } catch (error) {
      console.error('Failed to parse JSON:', error);
      return {};
    }
  },

  HeightManager: {
    init(element, cssVarName) {
      if (!element) return;

      element._heightManager = {
        cssVarName: cssVarName || '--slide-content-height',
        debounceTime: 250,
        debounceTimer: null,
      };

      requestAnimationFrame(() => this.updateHeight(element));

      const boundResizeHandler = () => this.debounceHeightUpdate(element);
      element._heightManager.boundResizeHandler = boundResizeHandler;
      window.addEventListener('resize', boundResizeHandler);

      return this;
    },

    debounceHeightUpdate(element) {
      if (!element._heightManager) return;

      clearTimeout(element._heightManager.debounceTimer);

      element._heightManager.debounceTimer = setTimeout(() => {
        this.updateHeight(element);
      }, element._heightManager.debounceTime);
    },

    updateHeight(element) {
      if (!element || !element._heightManager) return;

      if (window.innerWidth < 1024) {
        const subscriptionFlow = element.closest('subscription-flow');
        const header = subscriptionFlow?.querySelector('header');
        const footer = subscriptionFlow?.querySelector('footer');
        const content = element.querySelector('[data-content]');

        const headerHeight = header?.offsetHeight || 42;
        const footerHeight = footer?.offsetHeight || 74;
        const contentHeight = content?.offsetHeight || 0;

        const staticHeightsTotal = headerHeight + footerHeight + contentHeight;
        document.documentElement.style.setProperty(element._heightManager.cssVarName, `${staticHeightsTotal}px`);
      }
    },

    disconnect(element) {
      if (!element || !element._heightManager) return;

      clearTimeout(element._heightManager.debounceTimer);

      if (element._heightManager.boundResizeHandler) {
        window.removeEventListener('resize', element._heightManager.boundResizeHandler);
      }

      element._heightManager = null;
    },
  },

  setupCommonSlideEventListeners(component, updateCallback) {
    component.unsubscribe.push(
      subscribe('subscription-flow:state-update', (data) => {
        if (data.step && parseInt(component.dataset.step) === data.step) {
          requestAnimationFrame(() => {
            Utils.HeightManager.updateHeight(component);
            if (updateCallback) updateCallback(data);
          });
        }
      })
    );

    component.unsubscribe.push(
      subscribe('subscription-flow:change-step', (data) => {
        if (data.step && parseInt(component.dataset.step) === data.step) {
          setTimeout(() => {
            Utils.HeightManager.updateHeight(component);
            if (updateCallback) updateCallback(data);
          }, 10);
        }
      })
    );
  },
};

/**
 * Main Subscription Flow Component
 * Serves as the central coordinator for the subscription flow
 */
if (!customElements.get('subscription-flow')) {
  customElements.define(
    'subscription-flow',
    class SubscriptionFlow extends HTMLElement {
      constructor() {
        super();

        this.currentStep = 1;
        this.lastEventTime = 0;
        this.debounceTime = 50;
        this.unsubscribe = [];
        this.hasStoredVariant = false;

        this.data = {
          selectedVariant: null,
          variantDetails: null,
          selectedPlan: null,
          planDetails: null,
          step: 1,
          refill: {
            price: 0,
            sellingPlan: null,
          },
          currency: null,
          totalSteps: 0,
        };
      }

      connectedCallback() {
        this.initializeData();
        this.hasStoredVariant = !!sessionStorage.getItem('selected_case');
        this.setupEventListeners();

        if (!this.hasStoredVariant) {
          this.activateFirstStep();
        }

        this.publishInitData();

        if (this.hasStoredVariant) {
          setTimeout(() => {
            this.processStoredVariantThenNavigate();
          }, 100);
        } else {
          this.updateNavigationState();
        }
      }

      updateNavigationState() {
        if (this.prevNavButton && this.currentStep > 1) {
          this.prevNavButton.classList.add('tw-is-active');
          this.prevNavButton.setAttribute('aria-disabled', 'false');
        }

        this.subscriptionFlowSteps.forEach((stepElement) => {
          const stepIndex = parseInt(stepElement.dataset.step);
          stepElement.classList.toggle('tw-is-active', stepIndex <= this.currentStep);

          const stepText = stepElement.querySelector('[data-step-text]');
          if (stepText) {
            stepText.classList.toggle('tw-underline', stepIndex < this.currentStep);
          }
        });

        this.stepToggles.forEach((step) => {
          const stepIndex = parseInt(step.dataset.step);
          step.classList.toggle('tw-is-active', stepIndex === this.currentStep);
        });
      }

      checkForStoredVariant() {
        if (this.hasStoredVariant) {
          this.processStoredVariant();
        }
      }

      processStoredVariantThenNavigate() {
        const storedVariantId = sessionStorage.getItem('selected_case');
        if (!storedVariantId) return;

        const productSlide = this.querySelector('subscription-product-slide');
        if (!productSlide) {
          sessionStorage.removeItem('selected_case');
          return;
        }

        requestAnimationFrame(() => {
          const variantId = Number(storedVariantId);
          const variantInputs = productSlide.querySelectorAll('[data-case-input]');
          const matchingInput = Array.from(variantInputs).find((input) => Number(input.value) === variantId);

          if (matchingInput) {
            matchingInput.checked = true;

            const variantContainer = matchingInput.closest('[data-variant]');
            let variantDetails = {};

            if (variantContainer) {
              const variantDetailsElement = variantContainer.querySelector('[data-variant-details]');
              if (variantDetailsElement) {
                try {
                  variantDetails = Utils.parseJSON(variantDetailsElement.textContent);
                } catch (error) {
                  console.error('Error parsing variant details:', error);
                }
              }
            } else if (matchingInput.dataset.variants) {
              try {
                variantDetails = Utils.parseJSON(matchingInput.dataset.variants);
              } catch (error) {
                console.error('Error parsing variant details from input:', error);
              }
            }

            productSlide.dataset.selectedVariant = matchingInput.value;

            const form = matchingInput.closest('[data-variants-container]');
            if (form) {
              form.dispatchEvent(new Event('change', { bubbles: true }));
            }

            publish('subscription-flow:selected-variant', {
              selected_variant: matchingInput.value,
              variant_details: variantDetails,
            });

            requestAnimationFrame(() => {
              this.currentStep = 2;
              this.data.step = 2;
              this.dataset.currentStep = 2;

              this.mainSteps.forEach((stepElement) => {
                const stepIndex = parseInt(stepElement.dataset.step);
                stepElement.classList.toggle('tw-is-active', stepIndex === this.currentStep);
              });

              this.updateNavigationState();
              this.debouncedPublishState();
              sessionStorage.removeItem('selected_case');
            });
          } else {
            sessionStorage.removeItem('selected_case');
            this.updateNavigationState();
          }
        });
      }

      initializeData() {
        this.currentStep = parseInt(this.dataset.currentStep) || 1;
        this.refillPrice = parseFloat(this.dataset.refillPrice) || 0;
        this.currency = this.dataset.currency || 'USD';

        this.mainSteps = this.querySelectorAll('[data-main-step]');
        this.subscriptionFlowSteps = this.querySelectorAll('subscription-flow-step');
        this.stepToggles = this.querySelectorAll('step-toggle');
        this.prevNavButton = this.querySelector('[data-prev-slide-btn-container]');

        this.data.step = this.currentStep;
        this.data.refill.price = this.refillPrice;
        this.data.currency = this.currency;
        this.data.totalSteps = this.mainSteps.length;
      }

      setupEventListeners() {
        if (this.prevNavButton) {
          this.prevNavButton.addEventListener('click', () => {
            if (this.currentStep > 1) {
              publish('subscription-flow:change-step', {
                step: this.currentStep - 1,
                isCheckoutBtn: false,
              });
            }
          });
        }

        this.unsubscribe.push(
          subscribe('subscription-flow:change-step', (data) => {
            this.changeStep(data.step, data.isCheckoutBtn);
            this.debouncedPublishState();
          })
        );

        this.unsubscribe.push(
          subscribe('subscription-flow:selected-variant', (data) => {
            if (this.shouldSkipDueToDebounce()) return;

            this.data.selectedVariant = data.selected_variant;
            this.data.variantDetails = data.variant_details;
            this.debouncedPublishState();
          })
        );

        this.unsubscribe.push(
          subscribe('subscription-flow:selected-plan', (data) => {
            if (this.shouldSkipDueToDebounce()) return;

            this.data.selectedPlan = data;
            this.data.planDetails = data.details;
            this.debouncedPublishState();
          })
        );
      }

      shouldSkipDueToDebounce() {
        const now = Date.now();
        if (now - this.lastEventTime < this.debounceTime) {
          return true;
        }
        this.lastEventTime = now;
        return false;
      }

      debouncedPublishState() {
        if (this.publishTimeout) {
          clearTimeout(this.publishTimeout);
        }

        this.publishTimeout = setTimeout(() => {
          publish('subscription-flow:state-update', this.data);
        }, this.debounceTime);
      }

      activateFirstStep() {
        if (this.hasStoredVariant) {
          return;
        }

        if (this.mainSteps.length > 0) {
          this.mainSteps[0].classList.add('tw-is-active');
        }
      }

      publishInitData() {
        const subscriptionData = {
          step: this.currentStep,
          refill: {
            price: this.refillPrice,
            sellingPlan: this.data.refill.sellingPlan,
          },
          currency: this.currency,
          totalSteps: this.mainSteps.length,
        };

        publish('subscription-flow:init-data', subscriptionData);
      }

      changeStep(step, isCheckoutBtn) {
        this.currentStep = step;
        this.data.step = step;
        this.dataset.currentStep = step;

        this.mainSteps.forEach((stepElement) => {
          const stepIndex = parseInt(stepElement.dataset.step);
          stepElement.classList.toggle('tw-is-active', stepIndex === this.currentStep);
        });

        if (this.prevNavButton) {
          const isActive = this.currentStep > 1;
          this.prevNavButton.classList.toggle('tw-is-active', isActive);
          this.prevNavButton.setAttribute('aria-disabled', !isActive);
        }

        this.subscriptionFlowSteps.forEach((stepElement) => {
          const stepIndex = parseInt(stepElement.dataset.step);
          stepElement.classList.toggle('tw-is-active', stepIndex <= this.currentStep);

          const stepText = stepElement.querySelector('[data-step-text]');
          if (stepText) {
            stepText.classList.toggle('tw-underline', stepIndex < this.currentStep);
          }
        });

        this.stepToggles.forEach((step) => {
          const stepIndex = parseInt(step.dataset.step);
          step.classList.toggle('tw-is-active', stepIndex === this.currentStep);
        });
      }

      disconnectedCallback() {
        if (this.publishTimeout) {
          clearTimeout(this.publishTimeout);
        }

        this.unsubscribe.forEach((unsub) => {
          if (typeof unsub === 'function') unsub();
        });
      }
    }
  );
}

/**
 * Step Navigation Component
 * Controls navigation between subscription steps
 */
if (!customElements.get('subscription-flow-step')) {
  customElements.define(
    'subscription-flow-step',
    class SubscriptionFlowStep extends HTMLElement {
      constructor() {
        super();
        this.unsubscribe = [];
        this.handleClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        this.step = parseInt(this.dataset.step) || 1;
        this.isCheckoutBtn = this.dataset.checkoutBtn === 'true';

        this.setupEventListeners();
      }

      setupEventListeners() {
        this.unsubscribe.push(
          subscribe('subscription-flow:init-data', (data) => {
            this.updateStepState(data.totalSteps);
          })
        );

        this.unsubscribe.push(
          subscribe('subscription-flow:state-update', (data) => {
            if (data.step) {
              this.classList.toggle('tw-is-active', this.step <= data.step);

              const stepText = this.querySelector('[data-step-text]');
              if (stepText) {
                stepText.classList.toggle('tw-underline', this.step < data.step);
              }
            }
          })
        );

        this.addEventListener('click', this.handleClick);
      }

      handleClick() {
        if (this.classList.contains('tw-is-active')) {
          publish('subscription-flow:change-step', {
            step: this.step,
            isCheckoutBtn: this.isCheckoutBtn,
          });
        }
      }

      updateStepState(totalSteps) {
        this.classList.toggle('tw-is-active', this.step <= totalSteps);
      }

      disconnectedCallback() {
        this.unsubscribe.forEach((unsub) => {
          if (typeof unsub === 'function') unsub();
        });
        this.removeEventListener('click', this.handleClick);
      }
    }
  );
}

/**
 * Step Toggle Component
 * Used for toggling between specific steps and handling checkout
 */
if (!customElements.get('step-toggle')) {
  customElements.define(
    'step-toggle',
    class StepToggle extends HTMLElement {
      constructor() {
        super();
        this.unsubscribe = [];
        this.scentSelectionComplete = false;

        this.checkoutState = {
          selectedVariant: null,
          variantDetails: null,
          selectedPlan: null,
          planDetails: null,
          scentSelections: {},
          refillCount: 0,
          isComplete: false,
          currency: 'GBP',
          sellingPlanId: null,
        };

        this.toggleStep = this.toggleStep.bind(this);
      }

      connectedCallback() {
        this.setupProperties();
        this.setupButtonType();
        this.addEventListener('click', this.toggleStep);
      }

      setupProperties() {
        this.step = parseInt(this.dataset.step) + 1;
        this.isCheckoutBtn = this.hasAttribute('data-checkout-btn');
        this.isScentBtn = this.hasAttribute('data-scent-btn');
        this.buttonLabel = this.querySelector('.button__label');
        this.isCombinedScentCheckout = this.isCheckoutBtn && this.isScentBtn;
      }

      setupButtonType() {
        if (this.isCheckoutBtn) {
          this.initializeCheckout();
        }

        if (this.isScentBtn) {
          this.initializeScentSelection();
        }
      }

      initializeCheckout() {
        this.setInitialCurrency();
        this.initializeCheckoutState();
        this.setupCheckoutEventListeners();
      }

      setInitialCurrency() {
        const flow = this.closest('subscription-flow');
        if (flow) {
          this.checkoutState.currency = flow.dataset.currency || 'USD';
        }
      }

      initializeScentSelection() {
        const scentSlide = document.querySelector('subscription-scent-slide');

        if (this.isScentSlideAvailable(scentSlide)) {
          const isComplete = scentSlide.selectedRefills === scentSlide.refillLimit;
          this.scentSelectionComplete = isComplete;
          this.updateButtonState(isComplete, scentSlide.selectedRefills, scentSlide.refillLimit);
        } else {
          this.disableButton();
        }

        this.setupScentSelectionEvents();
      }

      isScentSlideAvailable(scentSlide) {
        return (
          scentSlide && typeof scentSlide.selectedRefills === 'number' && typeof scentSlide.refillLimit === 'number'
        );
      }

      disableButton() {
        this.classList.add('disabled');
        this.setAttribute('disabled', '');
      }

      initializeCheckoutState() {
        this.initializeProductVariantState();
        this.initializePlanState();
        this.initializeScentState();
      }

      initializeProductVariantState() {
        const productSlide = document.querySelector('subscription-product-slide');
        if (!productSlide) return;

        const selectedVariant = productSlide.dataset.selectedVariant;
        if (!selectedVariant) return;

        this.checkoutState.selectedVariant = selectedVariant;

        this.parseVariantDetails(productSlide);
      }

      parseVariantDetails(productSlide) {
        const variantDetailsElem = productSlide.querySelector('[data-variant-details]');
        if (!variantDetailsElem) return;

        try {
          this.checkoutState.variantDetails = JSON.parse(variantDetailsElem.textContent);
        } catch (error) {
          console.error('Error parsing variant details:', error);
        }
      }

      initializePlanState() {
        const activePlanElement = document.querySelector('subscription-plan [data-plan-toggle].tw-is-active');
        if (!activePlanElement) return;

        const planElement = activePlanElement.closest('subscription-plan');
        if (!planElement) return;

        const planDetailsElem = planElement.querySelector('[data-plan-details]');
        if (!planDetailsElem) return;

        this.parsePlanDetails(planDetailsElem);
      }

      parsePlanDetails(planDetailsElem) {
        try {
          const planDetails = JSON.parse(planDetailsElem.textContent);
          this.checkoutState.planDetails = planDetails;

          if (planDetails.selling_plan_id) {
            this.checkoutState.sellingPlanId = planDetails.selling_plan_id;
          }
        } catch (error) {
          console.error('Error parsing plan details:', error);
        }
      }

      initializeScentState() {
        const scentSlide = document.querySelector('subscription-scent-slide');
        if (!scentSlide || typeof scentSlide.variantSelections !== 'object') return;

        this.checkoutState.scentSelections = { ...scentSlide.variantSelections };
        this.checkoutState.refillCount = scentSlide.selectedRefills || 0;

        if (typeof scentSlide.refillLimit === 'number') {
          this.checkoutState.isComplete = scentSlide.selectedRefills === scentSlide.refillLimit;
        }

        this.updateCheckoutButtonState();
      }

      setupScentSelectionEvents() {
        this.unsubscribe.push(
          subscribe('subscription-flow:scent-selection', this.handleScentSelectionUpdate.bind(this))
        );
      }

      handleScentSelectionUpdate(data) {
        this.scentSelectionComplete = data.isComplete;
        this.updateButtonState(data.isComplete, data.count, data.limit);

        if (this.isCheckoutBtn) {
          this.updateCheckoutStateFromScentData(data);
        }
      }

      updateCheckoutStateFromScentData(data) {
        this.checkoutState.scentSelections = data.selections;
        this.checkoutState.refillCount = data.count;
        this.checkoutState.isComplete = data.isComplete;
        this.updateCheckoutButtonState();
      }

      setupCheckoutEventListeners() {
        this.setupStateUpdateListener();
        this.setupScentSelectionListener();
        this.setupPlanSelectionListener();
      }

      setupStateUpdateListener() {
        this.unsubscribe.push(subscribe('subscription-flow:state-update', this.handleFlowStateUpdate.bind(this)));
      }

      setupScentSelectionListener() {
        if (!this.isScentBtn) {
          this.unsubscribe.push(
            subscribe('subscription-flow:scent-selection', this.updateCheckoutStateFromScentData.bind(this))
          );
        }
      }

      setupPlanSelectionListener() {
        this.unsubscribe.push(subscribe('subscription-flow:selected-plan', this.handlePlanSelection.bind(this)));
      }

      handleFlowStateUpdate(data) {
        if (data.selectedVariant) this.checkoutState.selectedVariant = data.selectedVariant;
        if (data.variantDetails) this.checkoutState.variantDetails = data.variantDetails;
        if (data.selectedPlan) this.checkoutState.selectedPlan = data.selectedPlan;
        if (data.currency) this.checkoutState.currency = data.currency;

        if (data.planDetails) {
          this.checkoutState.planDetails = data.planDetails;
          this.updateSellingPlanFromDetails(data.planDetails);
        }
      }

      handlePlanSelection(data) {
        if (!data.details) return;

        this.checkoutState.planDetails = data.details;
        this.updateSellingPlanFromDetails(data.details);
      }

      updateSellingPlanFromDetails(planDetails) {
        this.checkoutState.sellingPlanId = planDetails.selling_plan_id || null;
      }

      updateCheckoutButtonState() {
        if (!this.isCheckoutBtn) return;

        const shouldDisable = !this.checkoutState.isComplete;
        this.classList.toggle('disabled', shouldDisable);
        this.toggleAttribute('disabled', shouldDisable);

        if (this.isCombinedScentCheckout && this.buttonLabel) {
          this.updateSpanVisibility(this.checkoutState.isComplete);
        }
      }

      updateButtonState(isComplete, selectedCount, totalLimit) {
        if (!this.isCheckoutBtn) {
          this.toggleButtonEnabled(isComplete);
        }

        const remainingCount = totalLimit - selectedCount;
        this.updateDataAttributes(selectedCount, totalLimit, remainingCount);
        this.updateCounterElements(remainingCount);

        if (!this.isCheckoutBtn || this.isCombinedScentCheckout) {
          this.updateSpanVisibility(isComplete);
        }
      }

      toggleButtonEnabled(isEnabled) {
        this.classList.toggle('disabled', !isEnabled);
        this.toggleAttribute('disabled', !isEnabled);
      }

      updateDataAttributes(selectedCount, totalLimit, remainingCount) {
        this.dataset.selectedCount = selectedCount;
        this.dataset.requiredCount = totalLimit;
        this.dataset.remainingCount = remainingCount;
      }

      updateCounterElements(remainingCount) {
        const remainingCountSpans = this.querySelectorAll('[data-remaining-variants-count]');
        remainingCountSpans.forEach((span) => {
          span.textContent = remainingCount;
        });
      }

      updateSpanVisibility(isComplete) {
        if (!this.buttonLabel) return;

        if (this.isScentBtn) {
          this.updateScentButtonSpans(isComplete);
        } else if (this.isCheckoutBtn) {
          this.updateCheckoutButtonSpans(isComplete);
        } else {
          this.updateRegularButtonSpans();
        }
      }

      updateScentButtonSpans(isComplete) {
        const regularSpans = this.buttonLabel.querySelectorAll('span:not([data-remaining-variants-text])');
        const remainingVariantsSpans = this.buttonLabel.querySelectorAll('[data-remaining-variants-text]');

        regularSpans.forEach((span) => {
          span.classList.toggle('tw-is-active', isComplete);
        });

        remainingVariantsSpans.forEach((span) => {
          span.classList.toggle('tw-is-active', !isComplete);
        });

        if (this.isCombinedScentCheckout) {
          const checkoutTextContainer = this.querySelector('.checkout-text');
          if (checkoutTextContainer) {
            checkoutTextContainer.classList.toggle('tw-is-active', isComplete);
          }
        }
      }

      updateCheckoutButtonSpans(isComplete) {
        const allSpans = this.buttonLabel.querySelectorAll('span');
        allSpans.forEach((span) => {
          const isRemainingVariantsText = span.hasAttribute('data-remaining-variants-text');
          span.classList.toggle('tw-is-active', isRemainingVariantsText ? !isComplete : isComplete);
        });
      }

      updateRegularButtonSpans() {
        const allSpans = this.buttonLabel.querySelectorAll('span');
        allSpans.forEach((span) => {
          span.classList.add('tw-is-active');
        });
      }

      async toggleStep(e) {
        if (this.isScentBtn && !this.scentSelectionComplete) {
          return this.handleIncompleteScentSelection(e);
        }

        if (this.isCheckoutBtn) {
          return this.handleCheckoutAction(e);
        }

        this.handleRegularStepToggle();
      }

      handleIncompleteScentSelection(e) {
        e.preventDefault();
        e.stopPropagation();

        publish('subscription-flow:selection-incomplete', {
          button: this,
          selectedCount: parseInt(this.dataset.selectedCount) || 0,
          requiredCount: parseInt(this.dataset.requiredCount) || 0,
        });

        return false;
      }

      async handleCheckoutAction(e) {
        e.preventDefault();

        if (this.isButtonDisabled()) {
          return;
        }

        this.setLoadingState();

        try {
          await this.processCheckout();
        } catch (error) {
          this.handleCheckoutError(error);
        }
      }

      isButtonDisabled() {
        return this.hasAttribute('disabled') || this.classList.contains('disabled');
      }

      setLoadingState() {
        this.setAttribute('disabled', 'true');
        this.classList.add('tw-is-loading');
      }

      handleCheckoutError(error) {
        console.error('Checkout error:', error);
        this.resetLoadingState();
      }

      resetLoadingState() {
        this.removeAttribute('disabled');
        this.classList.remove('tw-is-loading');
      }

      handleRegularStepToggle() {
        this.classList.toggle('!tw-is-active');

        publish('subscription-flow:change-step', {
          step: this.step,
          isCheckoutBtn: this.isCheckoutBtn,
        });
      }

      async processCheckout() {
        const uniqueBoxId = Date.now();
        const caseVariantId = this.determineCaseVariantId();

        if (!caseVariantId) {
          throw new Error('No case variant selected');
        }

        const isPlanDefault = this.checkoutState.planDetails?.plan_variant === 'default';
        const cartData = this.prepareCartData(uniqueBoxId, caseVariantId, isPlanDefault);

        this.notifyBeforeCheckout(cartData, uniqueBoxId);

        const cartResponse = await this.addToCart(cartData);
        console.log('Cart response:', cartResponse);

        window.location.href = '/checkout';
      }

      determineCaseVariantId() {
        if (!this.checkoutState.selectedVariant || !this.checkoutState.variantDetails) {
          return null;
        }

        const isPlanDefault = this.checkoutState.planDetails?.plan_variant === 'default';
        const isPlanOneOff = this.checkoutState.planDetails?.plan_variant === 'one_off';
        const isPlanOneOffTwo = this.checkoutState.planDetails?.plan_variant === 'one_off_2';

        if (isPlanOneOffTwo && this.checkoutState.variantDetails.one_off_2) {
          return this.checkoutState.variantDetails.one_off_2.id;
        }

        if ((isPlanOneOffTwo || isPlanOneOff) && this.checkoutState.variantDetails.one_off) {
          return this.checkoutState.variantDetails.one_off.id;
        }

        return this.checkoutState.selectedVariant;
      }

      prepareCartData(uniqueBoxId, caseVariantId, isPlanDefault) {
        const items = this.prepareCartItems(uniqueBoxId, caseVariantId, isPlanDefault);
        const attributes = this.collectAnalyticsAttributes();

        return { items, attributes };
      }

      prepareCartItems(uniqueBoxId, caseVariantId, isPlanDefault) {
        const items = [];

        this.addScentItemsToCart(items, uniqueBoxId, isPlanDefault);

        items.push({
          id: caseVariantId,
          quantity: 1,
          properties: {
            _unique_box_id: uniqueBoxId,
          },
        });

        return items;
      }

      addScentItemsToCart(items, uniqueBoxId, isPlanDefault) {
        Object.entries(this.checkoutState.scentSelections).forEach(([variantId, quantity]) => {
          if (quantity <= 0) return;

          const item = {
            id: variantId,
            quantity: quantity,
            properties: {
              _unique_box_id: uniqueBoxId,
            },
          };

          if (this.checkoutState.sellingPlanId && isPlanDefault) {
            item.selling_plan = this.checkoutState.sellingPlanId;
          }

          items.push(item);
        });
      }

      notifyBeforeCheckout(cartData, uniqueBoxId) {
        publish('subscription-flow:before-checkout', {
          items: cartData.items,
          uniqueBoxId: uniqueBoxId,
        });
      }

      collectAnalyticsAttributes() {
        return {
          _ga: this.getCookie('_ga'),
          _fbp: this.getCookie('_fbp'),
          _fbc: this.getCookie('_fbc'),
          '_gat_UA-168413491-5': this.getCookie('_gat_UA-168413491-5'),
        };
      }

      getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
      }

      async addToCart(data) {
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || 'Error adding items to cart');
          }

          return await response.json();
        } catch (error) {
          console.error('Cart error:', error);
          throw error;
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.toggleStep);

        this.unsubscribe.forEach((unsub) => {
          if (typeof unsub === 'function') unsub();
        });
      }
    }
  );
}
